import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, ExternalLink, CalendarDays } from 'lucide-react';
import { PHONE_TEL, SQUARE_BOOKING_URL, WHATSAPP_URL } from './data';
import { PayButton } from './PayButton';
import { useLanguage } from './i18n/LanguageContext';
type BookingModalProps = {
  open: boolean;
  onClose: () => void;
};
export function BookingModal({
  open,
  onClose
}: BookingModalProps) {
  const shouldReduceMotion = useReducedMotion();
  const {
    t
  } = useLanguage();
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open) {
      setLoaded(false);
      setBlocked(false);
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 60);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [open, onClose]);
  useEffect(() => {
    if (!open || loaded) return;
    const timer = window.setTimeout(() => setBlocked(true), 8000);
    return () => window.clearTimeout(timer);
  }, [open, loaded]);
  return <AnimatePresence data-id="element-64" data-mp="1weqoc7h57rxa">
      {open && <motion.div role="dialog" aria-modal="true" aria-label={t.booking.dialogLabel} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: shouldReduceMotion ? 0.12 : 0.24,
      ease: [0.22, 1, 0.36, 1]
    }} className="fixed inset-0 z-[70] flex items-end justify-center bg-dark/70 backdrop-blur-sm p-0 sm:items-center sm:p-6" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }} data-id="element-65" data-mp="1imah0uh7l7qk">
          <motion.div initial={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 28,
        scale: 0.985
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 20,
        scale: 0.99
      }} transition={{
        duration: shouldReduceMotion ? 0.12 : 0.32,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative flex h-[92vh] w-full flex-col overflow-hidden border border-metallic-border bg-warm-white shadow-[0_28px_70px_rgba(44,33,24,0.4)] sm:h-[86vh] sm:max-w-3xl" data-id="element-66" data-mp="krzq9p2z0c55">
            <header className="flex items-center justify-between gap-4 border-b border-brand-border bg-[#faf0e7] px-5 py-4 sm:px-7" data-id="element-67" data-mp="yiv136x153bd">
              <div className="min-w-0" data-id="element-68" data-mp="3j9lrrqmkpfmu">
                <p className="font-body text-[9px] font-semibold uppercase tracking-[4px] text-champagne-deep" data-id="element-69" data-mp="3j8kf5hpwsl30">
                  {t.booking.eyebrow}
                </p>
                <h2 className="truncate font-serif text-xl font-light text-dark sm:text-2xl" data-id="element-70" data-mp="23qeflo0mc5vr">
                  {t.booking.title}
                </h2>
              </div>
              <div className="flex shrink-0 items-center gap-2" data-id="element-71" data-mp="28no32y4g67gy">
                <PayButton data-id="element-72" data-mp="2dh93ewtijdjb" />
                <button ref={closeRef} type="button" onClick={onClose} aria-label={t.booking.close} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-metallic-border text-champagne-deep transition-colors hover:bg-champagne hover:text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne focus-visible:ring-offset-2 focus-visible:ring-offset-[#faf0e7]" data-id="element-73" data-mp="2dik60tb82f8u">
                  <X className="h-4 w-4" strokeWidth={1.7} data-id="element-74" data-mp="12129oevz0hwc" />
                </button>
              </div>
            </header>

            <div className="relative flex-1 bg-white" data-id="element-75" data-mp="2shmlnkopikak">
              <iframe src={SQUARE_BOOKING_URL} title={t.booking.iframeTitle} onLoad={() => setLoaded(true)} className="h-full w-full border-0" data-id="element-76" data-mp="2sk8xhwimrlyl" />

              {!loaded && <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-warm-white px-8 text-center" data-id="element-77" data-mp="2xitwxtq261lz">
                  <motion.span aria-hidden="true" animate={shouldReduceMotion ? undefined : {
              rotate: 360
            }} transition={{
              duration: 1.5,
              ease: 'linear',
              repeat: Infinity
            }} className="h-9 w-9 rounded-full border-2 border-gold-ultra-light border-t-champagne-deep" data-id="element-78" data-mp="29ptw608ujh7l" />
                  <p className="font-body text-[10px] uppercase tracking-[3px] text-soft" data-id="element-79" data-mp="1q2vx8w34h0ky">
                    {t.booking.loading}
                  </p>
                  {blocked && <div className="max-w-sm" data-id="element-80" data-mp="1by59ulm7cl06">
                      <p className="font-body text-sm font-light leading-relaxed text-mid" data-id="element-81" data-mp="1bwc4rar8m8ua">
                        {t.booking.blocked}
                      </p>
                      <a href={SQUARE_BOOKING_URL} target="_blank" rel="noreferrer" className="metallic-reserve-button mt-5 inline-flex items-center gap-2 px-8 py-3.5 font-body text-[10px] font-semibold uppercase tracking-[3px] text-white" data-id="element-82" data-mp="121ae2ahbcypl">
                        <CalendarDays className="h-3.5 w-3.5" strokeWidth={2} data-id="element-83" data-mp="1kqqpvtyg8dlb" />
                        {t.booking.openBooking}
                      </a>
                    </div>}
                </div>}
            </div>

            <footer className="flex flex-col items-center justify-between gap-2 border-t border-brand-border bg-[#faf0e7] px-5 py-3 text-center sm:flex-row sm:px-7 sm:text-left" data-id="element-84" data-mp="9a0hc46jz9oj">
              <p className="font-body text-[11px] font-light tracking-[0.5px] text-soft" data-id="element-85" data-mp="2gwaawj9u6jlv">
                {t.booking.footerPrefix}{' '}
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="font-medium text-champagne-deep underline-offset-4 hover:underline" data-id="element-86" data-mp="2c0btjfmxe1uy">
                  {t.booking.whatsappLabel}
                </a>{' '}
                ·{' '}
                <a href={`tel:${PHONE_TEL}`} className="font-medium text-champagne-deep underline-offset-4 hover:underline" data-id="element-87" data-mp="1t7zfhvmwtc4z">
                  {t.booking.callLabel}
                </a>
              </p>
              <a href={SQUARE_BOOKING_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-body text-[10px] font-semibold uppercase tracking-[2px] text-champagne-deep transition-colors hover:text-gold" data-id="element-88" data-mp="1jbeb9cips527">
                {t.booking.newTab}
                <ExternalLink className="h-3 w-3" strokeWidth={2} data-id="element-89" data-mp="3q5onzx3z1lgc" />
              </a>
            </footer>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}